import type { VarsityApp } from "@/model/app/varsity-app"
import { computed } from "vue"
import { useRouter } from "vue-router"

interface VarsityAppConfig {
    varsityApp: VarsityApp
    /** First route path segment, e.g. `/my-office/...`. Also used as the `id` / CSS class. */
    slug: string
    /** Human-readable label; only set for apps that show their own name in the UI. */
    label?: string
    /** Color for this app; currently used for the background of the "My Varsity EU" button */
    color?: string
}

const varsityApps: VarsityAppConfig[] = [
    { varsityApp: "MyVarsity", slug: "my-varsity" },
    { varsityApp: "MyStaff", slug: "my-staff" },
    { varsityApp: "MyOffice", slug: "my-office", label: "My Office", color: "#d4014b" },
    { varsityApp: "MyAccounting", slug: "my-accounting" },
    { varsityApp: "MyAdmin", slug: "my-admin", label: "My Admin", color: "#dc3545" },
    { varsityApp: "MyScoring", slug: "my-scoring", label: "My Scoring", color: "#008000" },
    { varsityApp: "DevTools", slug: "dev-tools" },
]

/**
 * Derives the currently active VarsityApp from the first segment of the route path
 * (e.g. `/my-office/...` => `MyOffice`) using {@link varsityApps} as the single source
 * of truth for slug, label and enum value. Returns `null` for unknown / non-app routes.
 */
export function useCurrentVarsityApp() {
    const router = useRouter()

    const currentVarsityAppConfig = computed((): VarsityAppConfig | null => {
        const routePart = router.currentRoute.value.path.split("/")[1]
        return varsityApps.find(app => app.slug === routePart) ?? null
    })

    const currentVarsityApp = computed((): VarsityApp | null => currentVarsityAppConfig.value?.varsityApp ?? null)

    /** Kebab-cased slug for the active app, e.g. `my-office`. `undefined` for non-app routes. */
    const currentVarsityAppSlug = computed(() => currentVarsityAppConfig.value?.slug)

    /** Human-readable label, e.g. `My Office`. `null` for apps without a dedicated label. */
    const currentVarsityAppLabel = computed(() => currentVarsityAppConfig.value?.label ?? null)

    const currentVarsityAppColor = computed(() => currentVarsityAppConfig.value?.color ?? null)

    return { currentVarsityApp, currentVarsityAppSlug, currentVarsityAppLabel, currentVarsityAppColor }
}
